import * as THREE from 'three';

const MAX_ACTIVE = 3;
const LIFE_S = 8;
const FALLBACK_DIST = 20;
const LABEL_LIFT = 1.35;

export type FpsPingType = 'danger' | 'loot' | 'go' | 'enemy' | 'default';

type PingStyle = {
  label: string;
  coneColor: number;
  sphereColor: number;
  borderCss: string;
  textCss: string;
};

const PING_STYLES: Record<FpsPingType, PingStyle> = {
  danger: {
    label: 'DANGER',
    coneColor: 0xff3030,
    sphereColor: 0xff6060,
    borderCss: 'rgba(255,70,70,0.75)',
    textCss: '#ffe0e0',
  },
  loot: {
    label: 'LOOT',
    coneColor: 0xffc040,
    sphereColor: 0xffe080,
    borderCss: 'rgba(255,196,80,0.75)',
    textCss: '#fff4d8',
  },
  go: {
    label: 'GO',
    coneColor: 0x40d8ff,
    sphereColor: 0x80eeff,
    borderCss: 'rgba(70,220,255,0.75)',
    textCss: '#e8fcff',
  },
  enemy: {
    label: 'ENEMY',
    coneColor: 0xff4a78,
    sphereColor: 0xff8a4a,
    borderCss: 'rgba(255,90,130,0.65)',
    textCss: '#ffe8f0',
  },
  default: {
    label: 'PING',
    coneColor: 0xff4a78,
    sphereColor: 0xff8a4a,
    borderCss: 'rgba(255,90,130,0.65)',
    textCss: '#ffe8f0',
  },
};

type ActivePing = {
  group: THREE.Group;
  label: HTMLDivElement;
  world: THREE.Vector3;
  life: number;
  type: FpsPingType;
};

export type FpsWorldPingOptions = {
  label?: string;
  type?: FpsPingType;
};

/** Resolve look-ray hit on world geometry, or a point `fallbackDist` m ahead. */
export function resolvePingLookTarget(
  camera: THREE.Camera,
  sceneRoot: THREE.Object3D,
  fallbackDist = FALLBACK_DIST,
): THREE.Vector3 {
  const origin = new THREE.Vector3();
  const dir = new THREE.Vector3();
  camera.getWorldPosition(origin);
  camera.getWorldDirection(dir);

  const raycaster = new THREE.Raycaster();
  raycaster.setFromCamera(new THREE.Vector2(0, 0), camera);
  raycaster.far = 120;
  const hits = raycaster.intersectObject(sceneRoot, true);
  const hit = hits[0];
  if (hit) {
    return hit.point.clone();
  }

  return origin.clone().addScaledVector(dir, fallbackDist);
}

export function getPingTypeLabel(type: FpsPingType): string {
  return PING_STYLES[type].label;
}

/**
 * Floating 3D ping markers in the world (pulsing cone + sphere) with optional projected CSS labels.
 */
export class FpsWorldPingMarkers {
  private readonly scene: THREE.Scene;
  private readonly root: HTMLDivElement;
  private readonly active: ActivePing[] = [];
  private readonly proj = new THREE.Vector3();
  private readonly sharedConeGeo = new THREE.ConeGeometry(0.42, 1.05, 10);
  private readonly sharedSphereGeo = new THREE.SphereGeometry(0.28, 12, 10);
  private disposed = false;

  constructor(scene: THREE.Scene, mount: HTMLElement) {
    this.scene = scene;
    this.root = document.createElement('div');
    this.root.style.cssText =
      'position:fixed;inset:0;pointer-events:none;z-index:38;overflow:hidden';
    mount.appendChild(this.root);
  }

  /** Spawn at world position; evicts oldest ping when at capacity. */
  spawnAt(world: THREE.Vector3, opts?: FpsWorldPingOptions): void {
    if (this.disposed) return;

    const type = opts?.type ?? 'default';
    const style = PING_STYLES[type];

    while (this.active.length >= MAX_ACTIVE) {
      this.removePing(0);
    }

    const group = new THREE.Group();
    group.position.copy(world);
    group.position.y += 0.18;

    const coneMat = new THREE.MeshBasicMaterial({
      color: style.coneColor,
      transparent: true,
      opacity: 0.88,
      depthWrite: false,
    });
    const sphereMat = new THREE.MeshBasicMaterial({
      color: style.sphereColor,
      transparent: true,
      opacity: 0.92,
      depthWrite: false,
    });

    const cone = new THREE.Mesh(this.sharedConeGeo, coneMat);
    cone.rotation.x = Math.PI;
    cone.position.y = 0.72;
    group.add(cone);

    const sphere = new THREE.Mesh(this.sharedSphereGeo, sphereMat);
    sphere.position.y = 1.42;
    group.add(sphere);

    this.scene.add(group);

    const label = document.createElement('div');
    label.textContent = opts?.label ?? style.label;
    label.style.cssText = [
      'position:absolute',
      'display:block',
      'transform:translate(-50%,-100%)',
      'padding:3px 8px',
      'border-radius:6px',
      `background:rgba(12,10,22,0.82)`,
      `border:1px solid ${style.borderCss}`,
      'font:800 10px system-ui,sans-serif',
      'letter-spacing:0.14em',
      `color:${style.textCss}`,
      'text-shadow:0 1px 3px rgba(0,0,0,0.75)',
      'pointer-events:none',
      'white-space:nowrap',
    ].join(';');
    this.root.appendChild(label);

    this.active.push({
      group,
      label,
      world: world.clone().add(new THREE.Vector3(0, LABEL_LIFT, 0)),
      life: LIFE_S,
      type,
    });
  }

  spawnFromLook(
    camera: THREE.Camera,
    sceneRoot: THREE.Object3D,
    opts?: FpsWorldPingOptions,
  ): THREE.Vector3 {
    const target = resolvePingLookTarget(camera, sceneRoot);
    this.spawnAt(target, opts);
    return target;
  }

  update(dt: number, camera: THREE.Camera, width: number, height: number): void {
    if (this.disposed) return;

    const t = performance.now() * 0.003;
    for (let i = this.active.length - 1; i >= 0; i--) {
      const p = this.active[i]!;
      p.life -= dt;
      if (p.life <= 0) {
        this.removePing(i);
        continue;
      }

      const fade = Math.min(1, p.life / 1.2);
      const pulse = 0.88 + Math.sin(t * 4.2 + i * 1.7) * 0.14;
      p.group.scale.setScalar(pulse);
      p.group.rotation.y += dt * 1.6;

      for (const child of p.group.children) {
        if (!(child instanceof THREE.Mesh)) continue;
        const mat = child.material as THREE.MeshBasicMaterial;
        mat.opacity = fade * (child.position.y < 1 ? 0.88 : 0.92);
      }

      this.proj.copy(p.world);
      this.proj.project(camera);
      const behind = this.proj.z > 1;
      if (behind) {
        p.label.style.display = 'none';
        continue;
      }

      const sx = (this.proj.x * 0.5 + 0.5) * width;
      const sy = (-this.proj.y * 0.5 + 0.5) * height;
      p.label.style.display = 'block';
      p.label.style.left = `${sx}px`;
      p.label.style.top = `${sy}px`;
      p.label.style.opacity = String(fade);
    }
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    while (this.active.length > 0) {
      this.removePing(0);
    }
    this.sharedConeGeo.dispose();
    this.sharedSphereGeo.dispose();
    this.root.remove();
  }

  private removePing(index: number): void {
    const p = this.active[index];
    if (!p) return;

    this.scene.remove(p.group);
    for (const child of p.group.children) {
      if (child instanceof THREE.Mesh) {
        (child.material as THREE.Material).dispose();
      }
    }
    p.label.remove();
    this.active.splice(index, 1);
  }
}
